import * as Sentry from '@sentry/browser';
import { AUTHENTICATION_START, AUTHENTICATION_FAILED, AUTHENTICATION_SUCCESS, AUTHENTICATION_REFRESH, AUTHENTICATION_ANONYMOUS_LOGIN_START, AUTHENTICATION_ANONYMOUS_LOGIN_SUCCESS, AUTHENTICATION_ANONYMOUS_LOGIN_FAILED, AUTHENTICATION_EXPIRED, } from './constants.js';
import { isActiveJwt, parseJwt } from './utils.js';
import { getCurrentRefreshInterval, scheduleRefreshByToken, unscheduleRefresh, updateTimeDriftByToken, } from './tokenRefreshScheduler.js';
import { authGlobals } from './authGlobals.js';
import { config } from './config.js';
export const getLocalStoragePrefix = () => `auth.${config.auth.openID.token}.${config.auth.client_id}`;
export const getRedirectUriStorageKey = () => `${getLocalStoragePrefix()}.redirect_uri`;
export const getRefreshTokenStorageKey = () => `${getLocalStoragePrefix()}.refresh_token`;
export const getLastBackgroundLoginStorageKey = () => `${getLocalStoragePrefix()}.lastBackgroundLogin`;
const MAX_SAFE_URL_LENGTH = 2000;
// keycloak scope settings for logout id_token
const scope = 'openid profile email';
const getLoginHref = () => {
    const currentUrl = new URL(window.location.href);
    const searchParams = currentUrl.searchParams;
    // clean this two search params from URL, it breaks Cobra (Keycloak)
    searchParams.delete('session_state');
    searchParams.delete('code');
    let redirect_uri = currentUrl.href;
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: config.auth.client_id,
        redirect_uri,
        scope,
    });
    let loginHref = `${config.auth.openID.authentication}?${params}`;
    if (loginHref.length > MAX_SAFE_URL_LENGTH) {
        currentUrl.hash = '';
        redirect_uri = currentUrl.href;
        params.set('redirect_uri', redirect_uri);
        loginHref = `${config.auth.openID.authentication}?${params}`;
    }
    return {
        loginHref,
        redirect_uri,
    };
};
export const actionCobraLogin = () => {
    const { loginHref, redirect_uri } = getLoginHref();
    window.localStorage.setItem(getRedirectUriStorageKey(), redirect_uri);
    window.location.href = loginHref;
};
/**
 * Set token to store
 */
export const actionCobraAuth = (payload, onReplaceUrl) => async (dispatch) => {
    const redirect_uri = window.localStorage.getItem(getRedirectUriStorageKey());
    if (!redirect_uri)
        return false;
    dispatch({ type: AUTHENTICATION_START });
    try {
        window.localStorage.removeItem(getRedirectUriStorageKey());
        const body = new URLSearchParams({
            code: payload.code,
            client_id: config.auth.client_id,
            grant_type: 'authorization_code',
            redirect_uri,
        });
        const requestTimestamp = Date.now();
        const authResponse = await fetch(config.auth.openID.token, {
            method: 'POST',
            credentials: 'omit',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body,
        });
        const { access_token, refresh_token, id_token } = await authResponse.json();
        const parsedAccessToken = parseJwt(access_token);
        const parsedRefreshToken = parseJwt(refresh_token);
        const parsedIdToken = parseJwt(id_token);
        if (!parsedAccessToken)
            throw new Error('Invalid access token');
        Sentry.setUser({
            ...parsedAccessToken.payload,
            id: parsedAccessToken.payload.preferred_username,
            username: parsedAccessToken.payload.preferred_username,
        });
        if (!parsedRefreshToken)
            throw new Error('Invalid refresh token');
        if (!parsedIdToken)
            throw new Error('Invalid id token');
        updateTimeDriftByToken(requestTimestamp, parsedRefreshToken);
        window.localStorage.setItem(getRefreshTokenStorageKey(), refresh_token);
        dispatch(actionAuthenticationSuccess(access_token, parsedAccessToken, id_token));
        // TODO: shedule only by refresh token,
        // let update acces token if needed by `actionUseAccessToken`
        scheduleRefreshByToken(dispatch, parsedAccessToken);
        scheduleRefreshByToken(dispatch, parsedRefreshToken);
        return true;
    }
    catch (error) {
        dispatch({ type: AUTHENTICATION_FAILED, payload: error, error: true });
        return false;
    }
    finally {
        if (onReplaceUrl) {
            onReplaceUrl(redirect_uri);
        }
        else {
            window.history.replaceState(window.history.state, '', redirect_uri);
        }
    }
};
export const actionCobraRefreshToken = () => async (dispatch, getState) => {
    const currentRefreshToken = window.localStorage.getItem(getRefreshTokenStorageKey());
    if (!currentRefreshToken) {
        dispatch({ type: AUTHENTICATION_EXPIRED });
        return false;
    }
    const parsedCurrentRefreshToken = parseJwt(currentRefreshToken);
    if (!isActiveJwt(parsedCurrentRefreshToken)) {
        window.localStorage.removeItem(getRefreshTokenStorageKey());
        console.info('Discarding expired refresh token', parsedCurrentRefreshToken);
        dispatch({ type: AUTHENTICATION_EXPIRED });
        return false;
    }
    const body = new URLSearchParams({
        refresh_token: currentRefreshToken,
        client_id: config.auth.client_id,
        grant_type: 'refresh_token',
        scope,
    });
    try {
        const requestTimestamp = Date.now();
        const authResponse = await fetch(config.auth.openID.token, {
            method: 'POST',
            credentials: 'omit',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body,
        });
        const { access_token, refresh_token, id_token } = await authResponse.json();
        const parsedAccessToken = parseJwt(access_token);
        const parsedRefreshToken = parseJwt(refresh_token);
        const parsedIdToken = parseJwt(id_token);
        if (!parsedAccessToken) {
            throw new Error('Invalid access token');
        }
        Sentry.setUser({
            ...parsedAccessToken.payload,
            id: parsedAccessToken.payload.preferred_username,
            username: parsedAccessToken.payload.preferred_username,
        });
        if (!parsedRefreshToken) {
            throw new Error('Invalid refresh token');
        }
        if (!parsedIdToken) {
            throw new Error('Invalid id token');
        }
        updateTimeDriftByToken(requestTimestamp, parsedRefreshToken);
        window.localStorage.setItem(getRefreshTokenStorageKey(), refresh_token);
        authGlobals.token = access_token;
        authGlobals.id_token = id_token;
        dispatch({
            type: AUTHENTICATION_REFRESH,
            payload: { access_token, parsedAccessToken, id_token },
        });
        // TODO: shedule only by refresh token,
        // let update acces token if needed by `actionUseAccessToken`
        scheduleRefreshByToken(dispatch, parsedAccessToken);
        scheduleRefreshByToken(dispatch, parsedRefreshToken);
        return true;
    }
    catch (error) {
        console.error(error);
        /**
         * pokud selze obnova tokenu, a zaroven jeho obnoveni neni kratsi nez 10s
         * nastartuj opetovne scheduler na obnovu podle access tokenu
         * jinak ukonci a prepni uzivatele na neprihlaseneho
         */
        const state = getState();
        const parsedCurrentToken = state.auth.parsedAccessToken;
        if (parsedCurrentToken) {
            const expirationTime = parsedCurrentToken.payload.exp;
            if (expirationTime && getCurrentRefreshInterval(expirationTime) > 10) {
                scheduleRefreshByToken(dispatch, parsedCurrentToken);
                return false;
            }
        }
        window.localStorage.removeItem(getRefreshTokenStorageKey());
        dispatch({ type: AUTHENTICATION_EXPIRED });
        return false;
    }
};
export const actionAuthenticationSuccess = (access_token, parsedAccessToken, id_token) => {
    authGlobals.token = access_token;
    authGlobals.id_token = id_token;
    window.localStorage.removeItem(getLastBackgroundLoginStorageKey());
    return {
        type: AUTHENTICATION_SUCCESS,
        payload: { access_token, parsedAccessToken, id_token },
    };
};
export function actionUseAccessToken(callback) {
    return async (dispatch, getState) => {
        let state = getState();
        if (!isActiveJwt(state.auth.parsedAccessToken)) {
            await dispatch(actionCobraRefreshToken());
            state = getState();
        }
        if (state.auth.parsedAccessToken) {
            scheduleRefreshByToken(dispatch, state.auth.parsedAccessToken);
        }
        return callback(state.auth.access_token);
    };
}
export const clearCredentials = () => {
    const refresh_token = window.localStorage.getItem(getRefreshTokenStorageKey());
    if (!refresh_token)
        return null;
    unscheduleRefresh();
    window.localStorage.removeItem(getRefreshTokenStorageKey());
    return refresh_token;
};
export const actionCobraLogout = () => {
    const tokenHint = authGlobals.id_token;
    authGlobals.token = null;
    authGlobals.id_token = null;
    clearCredentials();
    Sentry.setUser(null);
    let logoutUrl = config.auth.openID.logout;
    if (tokenHint) {
        const currentUrl = new URL(window.location.href);
        if (currentUrl.href.length > MAX_SAFE_URL_LENGTH) {
            currentUrl.hash = '';
        }
        const params = new URLSearchParams({
            id_token_hint: tokenHint,
            post_logout_redirect_uri: currentUrl.href,
        });
        logoutUrl += `?${params}`;
    }
    window.location.href = logoutUrl;
    return true;
};
export const actionCobraTryBackgroundLogin = () => async (dispatch) => {
    const lastBackgroundLogin = window.localStorage.getItem(getLastBackgroundLoginStorageKey());
    if (lastBackgroundLogin) {
        const lastBackgroundLoginDate = new Date(parseInt(lastBackgroundLogin, 10));
        const now = new Date();
        const timeDiff = now.getTime() - lastBackgroundLoginDate.getTime();
        const diffSeconds = Math.ceil(timeDiff / 1000);
        console.warn(`[actionCobraTryBackgroundLogin] Background login tried ${diffSeconds} seconds ago and was not successfull`);
        return false;
    }
    const { loginHref } = getLoginHref();
    try {
        const response = await fetch(loginHref, {
            credentials: 'include',
            mode: 'cors',
            redirect: 'manual',
        });
        const isUserLoggedIn = (response.status >= 300 && response.status < 400) || response.type === 'opaqueredirect';
        if (isUserLoggedIn) {
            console.info(`[actionCobraTryBackgroundLogin] Background login...`);
            window.localStorage.setItem(getLastBackgroundLoginStorageKey(), Date.now().toString());
            dispatch(actionCobraLogin);
            return true;
        }
        else {
            console.info(`[actionCobraTryBackgroundLogin] User not logged in`);
        }
    }
    catch (error) {
        console.info(`[actionCobraTryBackgroundLogin] Cannot verify if user is logged in: ${error}`);
    }
    return false;
};
export const actionCobraLoginAsAnonymous = () => async (dispatch) => {
    try {
        const tokenUrl = config?.auth?.openID?.token;
        if (!tokenUrl) {
            return false;
        }
        dispatch({ type: AUTHENTICATION_ANONYMOUS_LOGIN_START });
        const body = new URLSearchParams();
        body.append('client_id', 'crystal');
        body.append('username', 'anonymous');
        body.append('password', 'Verejne!h3slo-pro-anonyma.');
        body.append('grant_type', 'password');
        body.append('scope', scope);
        const response = await fetch(tokenUrl, {
            method: 'POST',
            body,
            credentials: 'omit',
            headers: {
                Accept: 'application/json',
            },
        });
        if (!response.ok) {
            console.error('[actionCobraLoginAsAnonymous] Error', response.status, response.statusText);
            dispatch({
                type: AUTHENTICATION_ANONYMOUS_LOGIN_FAILED,
                payload: `HTTP ${response.status} ${response.statusText}`,
                error: true,
            });
            return false;
        }
        const data = await response.json();
        const { access_token, refresh_token } = data;
        authGlobals.token = access_token;
        scheduleRefreshByToken(dispatch, parseJwt(refresh_token));
        window.localStorage.setItem(getRefreshTokenStorageKey(), refresh_token);
        dispatch({ type: AUTHENTICATION_ANONYMOUS_LOGIN_SUCCESS, payload: data });
        return true;
    }
    catch (error) {
        console.error('[actionCobraLoginAsAnonymous] Error', error);
        dispatch({ type: AUTHENTICATION_ANONYMOUS_LOGIN_FAILED, payload: error, error: true });
        return false;
    }
};
//# sourceMappingURL=actions.js.map